import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from 'react-relay';

import { ItemFragment$data } from '../../graphql/__generated__/ItemFragment.graphql';
import { ListItemsQuery$variables } from '../../graphql/__generated__/ListItemsQuery.graphql';
import CreateListItemMutation from '../../graphql/mutations/CreateListItemMutation';
import useCommitItem from '../../hooks/useCommitItem';
import useDispatchSetterStore from '../../hooks/useDispatchSetterStore';
import useDynamicSelector from '../../hooks/useDynamicSelector';
import { SmxPropsContext } from '../../providers/propsProvider';
import { getSetterListItem, IListItemState } from '../../store/slices/listItemSlice';
import { ListItemType } from '../../types';
import LoaderListItem from './LoaderListItem';
import useExternalHandler from './useExternalHandler';

export type ParentRefType = {
    getCurrentItem: () => ListItemType;
    setChild: (newChildId: number) => void;
};

export interface ICommonLoaderListItem {
    parentNameList?: string;
    subRef: React.ForwardedRef<any>;
}

const CommonLoaderListItem: React.FC<ICommonLoaderListItem> = ({ parentNameList, subRef }) => {
    const contextProps = useContext(SmxPropsContext);
    const setterListItem = useDispatchSetterStore<IListItemState>(
        getSetterListItem(contextProps.nameList + contextProps.prefixStore),
    );
    const [commitCreate] = useMutation(CreateListItemMutation);
    const { getCommitItem } = useCommitItem();
    const parentItem = useDynamicSelector('listItem', 'parentItem');
    const parentDynamicItem = useDynamicSelector('listItem', 'parentDynamicItem');
    const originalItems = useDynamicSelector('listItem', 'originalItems');

    const [dataFromServer, setDataFromServer] = useState<{
        rows: any[];
        count: number;
        connectionId: string;
    }>({ rows: [], count: 0, connectionId: '' });

    useExternalHandler(subRef, dataFromServer.connectionId);

    useEffect(() => {
        setterListItem({
            originalItems: dataFromServer.rows,
            connectionId: dataFromServer.connectionId,
        });
    }, [dataFromServer]);

    useEffect(() => {
        if (parentItem || parentDynamicItem) {
            const childList = new Set(parentItem?.child?.split(','));
            setterListItem({
                items: originalItems.filter((item) => {
                    return childList.has(item.id.toString());
                }),
                currentEditItem: null,
                currentItem: null,
                currentItemId: null,
                editMode: 'viewing',
            });
        } else {
            setterListItem({
                items: [...originalItems],
            });
        }
    }, [parentItem, originalItems, parentDynamicItem]);

    const varRequest: ListItemsQuery$variables = { type: contextProps.nameList };

    if (parentItem) {
        varRequest.filter_flexible = `[{"or":[{"id":"${parentItem?.child.split(',').join('|')}"}]}]`;
    }

    return (
        <>
            <LoaderListItem
                onUpdateData={(rows: any[], count: number, connectionId: string) => {
                    setDataFromServer({ rows, count, connectionId });
                }}
                varRequest={varRequest}
            />
            {parentNameList && parentDynamicItem?.id && (
                <LoaderListItem
                    onUpdateData={(rows: any[], count: number, connectionId: string) => {
                        const [firstRow] = rows;

                        if (!firstRow) {
                            const data: Partial<ListItemType> = {
                                type: parentNameList,
                                link_id: parentDynamicItem.id,
                                name: `${parentNameList} ${parentDynamicItem.name ? parentDynamicItem.name : parentDynamicItem.id}`,
                                child: '0',
                            };

                            commitCreate(
                                getCommitItem(data as ItemFragment$data, connectionId, undefined, (id?: number) => {
                                    setterListItem({
                                        parentItem: {
                                            ...data,
                                            id,
                                        } as ListItemType,
                                    });
                                }),
                            );
                        }

                        setterListItem({
                            connectionParentId: connectionId,
                            parentItem: firstRow,
                        });
                    }}
                    varRequest={{
                        type: parentNameList,
                        link_id: `${parentDynamicItem.id}`,
                    }}
                />
            )}
        </>
    );
};

export default CommonLoaderListItem;
